import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Description,
  Field,
  FieldGroup,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { Box } from '@/components/ui/v3/Box';
import { useMatter } from '@/hooks/useMatter';
import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MedicalProvider } from '@/services/types/treatment-types';

interface AddLogEntryFormProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  onCancel: () => void;
  onSuccess: () => void;
  autoFocus?: boolean;
}

export const AddLogEntryForm = ({
  request,
  provider,
  onCancel,
  onSuccess,
  autoFocus = false,
}: AddLogEntryFormProps) => {
  const { matterId } = useMatter();
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [updateAuditLog] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsAuditLogsCreateMutation();

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create a new audit log entry with manual note
      await updateAuditLog({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        createTreatmentRecordRequestAuditLogSchema: {
          details: note,
          actor: 'user',
          request_id: request.id,
          previous_status: request.status || 'not_ready',
          new_status: request.status || 'not_ready',
        },
      }).unwrap();

      toast.success('Note added to activity log');
      setNote('');
      onSuccess();
    } catch (error) {
      console.error('Failed to add log entry:', error);
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="mb-4">
      <FieldGroup>
        <Field>
          <Label>Add Note to Activity Log</Label>
          <Description>
            Add a manual note or comment to the activity log for this request.
          </Description>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter your note here..."
            rows={3}
            autoFocus={autoFocus}
          />
        </Field>
      </FieldGroup>

      <div className="flex items-center gap-2 mt-4">
        <Button onClick={handleSubmit} disabled={!note.trim() || isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Note'}
        </Button>
        <Button outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Box>
  );
};
